<script lang="ts">
	import { page } from '$app/state';
	import Pre from '#lib/html/input/Pre.svelte';
	import { activityStore } from '#lib/stores/activity.js';
	import { agendaSettingStore } from '#lib/stores/agenda.js';
	import { areaStore } from '#lib/stores/area.js';
	import { blockingPeriods } from '#lib/stores/blocking-period.js';
	import { bookingStore } from '#lib/stores/booking.js';
	import { businessStore } from '#lib/stores/business.js';
	import { customerStore } from '#lib/stores/customer.js';
	import { featureFlipStore } from '#lib/stores/feature-flip.js';
	import { giftcardStore } from '#lib/stores/giftcard.js';
	import { t } from '#lib/stores/i18n.js';
	import { notificationSettingStore } from '#lib/stores/notification-setting.js';
	import { offerStore } from '#lib/stores/offer.js';
	import { openingHourExceptionStore } from '#lib/stores/opening-hour-exception.js';
	import { sessionStore } from '#lib/stores/session.js';
	import { sessionInstanceStore } from '#lib/stores/session-instance.js';
	import { userStore } from '#lib/stores/user.js';
	import { Database, Monitor } from '@lucide/svelte';
	import { onMount } from 'svelte';

	let selectedStoreKey = $state('user');

	let browserInfo = $state({
		userAgent: '',
		language: '',
		platform: '',
		vendor: '',
		screen: { width: 0, height: 0 },
		window: { innerWidth: 0, innerHeight: 0 }
	});

	onMount(() => {
		if (typeof window !== 'undefined') {
			browserInfo = {
				userAgent: navigator.userAgent,
				language: navigator.language,
				platform: navigator.platform,
				vendor: navigator.vendor,
				screen: {
					width: window.screen.width,
					height: window.screen.height
				},
				window: {
					innerWidth: window.innerWidth,
					innerHeight: window.innerHeight
				}
			};
		}
	});

	const STORE_OPTIONS = [
		{ key: 'user', label: 'User Store' },
		{ key: 'business', label: 'Business Store' },
		{ key: 'booking', label: 'Booking Store' },
		{ key: 'session', label: 'Session Store' },
		{ key: 'activity', label: 'Activity Store' },
		{ key: 'area', label: 'Area Store' },
		{ key: 'offer', label: 'Offer Store' },
		{ key: 'giftcard', label: 'Giftcard Store' },
		{ key: 'customer', label: 'Customer Store' },
		{ key: 'featureFlip', label: 'Feature Flips' },
		{ key: 'blockingPeriods', label: 'Blocking Periods' },
		{ key: 'sessionInstance', label: 'Session Instance' },
		{ key: 'agendaSettings', label: 'Agenda Settings' },
		{ key: 'notificationSettings', label: 'Notification Settings' },
		{ key: 'openingHourExceptions', label: 'Opening Hour Exceptions' },
		{ key: 'page', label: 'Page State' }
	];

	let currentStoreData = $derived.by(() => {
		switch (selectedStoreKey) {
			case 'user':
				return $userStore;
			case 'business':
				return $businessStore;
			case 'booking':
				return $bookingStore;
			case 'session':
				return $sessionStore;
			case 'activity':
				return $activityStore;
			case 'area':
				return $areaStore;
			case 'offer':
				return $offerStore;
			case 'giftcard':
				return $giftcardStore;
			case 'customer':
				return $customerStore;
			case 'featureFlip':
				return $featureFlipStore;
			case 'blockingPeriods':
				return $blockingPeriods;
			case 'sessionInstance':
				return $sessionInstanceStore;
			case 'agendaSettings':
				return $agendaSettingStore;
			case 'notificationSettings':
				return $notificationSettingStore;
			case 'openingHourExceptions':
				return $openingHourExceptionStore;
			case 'page':
				return { url: page.url.href, pathname: page.url.pathname, params: page.params };
			default:
				return null;
		}
	});
</script>

<div class="space-y-4">
	<!-- Device Info Compact Bar -->
	<div
		class="p-3.5 rounded-2xl border border-border/60 dark:border-white/10 bg-surface-on/3 dark:bg-white/3 flex items-center justify-between gap-3 flex-wrap text-2xs"
	>
		<div class="flex items-center gap-2 text-surface-on/70 dark:text-slate-300 font-semibold">
			<Monitor class="w-4 h-4 text-admin" />
			<span>{$t('diagnostics.state.device_title')}</span>
		</div>
		<div class="flex items-center gap-4 text-surface-on/60 dark:text-slate-400 font-mono">
			<span
				>{$t('diagnostics.state.screen_resolution')}: {browserInfo.screen
					.width}x{browserInfo.screen.height}</span
			>
			<span
				>{$t('diagnostics.state.window_size')}: {browserInfo.window.innerWidth}x{browserInfo
					.window.innerHeight}</span
			>
			<span>{browserInfo.language}</span>
		</div>
	</div>

	<!-- Store Selector -->
	<div class="flex items-center justify-between gap-3 flex-wrap">
		<label
			for="store-inspector-select"
			class="text-xs font-bold text-surface-on/70 dark:text-slate-300 uppercase tracking-wider flex items-center gap-1.5"
		>
			<Database class="w-3.5 h-3.5 text-admin" />
			{$t('diagnostics.state.inspect_store')}
		</label>
		<select
			id="store-inspector-select"
			bind:value={selectedStoreKey}
			class="px-3 py-1.5 text-xs rounded-xl border border-border/80 dark:border-white/15 bg-surface dark:bg-black text-surface-on dark:text-white font-medium cursor-pointer focus:outline-hidden focus:border-admin"
		>
			{#each STORE_OPTIONS as opt}
				<option value={opt.key}>{opt.label}</option>
			{/each}
		</select>
	</div>

	<!-- Store JSON Viewer -->
	<div
		class="rounded-2xl bg-black text-emerald-400 p-4 border border-white/15 font-mono text-2xs shadow-inner max-h-[50vh] overflow-y-auto"
	>
		<Pre>{JSON.stringify(currentStoreData, null, 2)}</Pre>
	</div>
</div>
